import { StaggeredAnimation } from "#components/animation/StaggeredAnimation.tsx";
import { StaggeredAnimationItem } from "#components/animation/StaggeredAnimationItem.tsx";
import { ConnectorChannelIcon } from "#components/ui/ConnectorChannelIcon.tsx";
import { useGetChannelsDetailsSuspenseQuery } from "#features/channels/api/useGetChannelsDetailsSuspenseQuery.ts";
import { useConnectIntegration } from "#features/conversations/hooks/useConnectIntegration.ts";
import { getIntegrationManifest } from "#features/conversations/integrations/index.ts";
import { integrationRowKey } from "#features/conversations/lib/connectionRows.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { useTracking } from "#hooks/useTracking.ts";
import { MixpanelEvent } from "#types/tracking.ts";
import type { ConnectedIntegration, ConnectorChannelType } from "#types/types.ts";
import { Button, Div, Flex } from "@hopper-ui/components";
import { CheckmarkIcon } from "@hopper-ui/icons";
import type { ColorValue } from "@hopper-ui/styled-system";
import { useCallback, useMemo } from "react";
import "./AssistantMessageConnectCard.css";

interface ConnectIntegrationRow {
    integration: ConnectedIntegration;
    displayName: string;
    channelType: ConnectorChannelType;
    brandColor: string;
    brandForegroundColor?: ColorValue;
    cardDescriptionKey: string;
}

interface Props {
    integrations: ConnectedIntegration[];
    // Set while the request's pause is open: a row can then be dismissed, and a dismissed one says so.
    dismissedKeys?: ReadonlySet<string>;
    onDismiss?: (rowKey: string) => void;
}

export const AssistantMessageConnectIntegration = ({ integrations, dismissedKeys, onDismiss }: Props) => {
    const { tShared } = useSharedTranslation();
    const { trackEvent } = useTracking();
    const connect = useConnectIntegration();
    // The channel-details query is already resolved by the time a conversation renders, so we read it
    // directly (no Suspense boundary needed); refetchOnWindowFocus keeps it fresh after the user connects.
    const { data: { personalIntegrations } } = useGetChannelsDetailsSuspenseQuery();

    const rows = useMemo<ConnectIntegrationRow[]>(
        () =>
            [...new Set(integrations)].flatMap(integration => {
                const manifest = getIntegrationManifest(integration);

                if (!manifest) {
                    return [];
                }

                const { displayName, channelType, brandColor, brandForegroundColor, cardDescriptionKey } = manifest;

                return [{ integration, displayName, channelType, brandColor, brandForegroundColor, cardDescriptionKey }];
            }),
        [integrations]
    );

    const connectedChannelTypes = useMemo(
        () => new Set(personalIntegrations.filter(x => x.isConnected).map(x => x.channelType)),
        [personalIntegrations]
    );

    // Connected, but missing permissions the integration can request on reconnect.
    const partiallyConnectedChannelTypes = useMemo(
        () => new Set(personalIntegrations.filter(x => x.isConnected && x.isPartiallyConnected).map(x => x.channelType)),
        [personalIntegrations]
    );

    // An org admin can turn off personal integrations; GetChannelsDetails then reports canConnect=false.
    // Only treat a channel as unavailable when the backend says so explicitly — an absent channel keeps
    // the default connectable behavior, so this can't regress a normally-connectable integration.
    const unavailableChannelTypes = useMemo(
        () => new Set(personalIntegrations.filter(x => !x.isConnected && !x.canConnect).map(x => x.channelType)),
        [personalIntegrations]
    );

    const handleConnect = useCallback((integration: ConnectedIntegration, trigger: "Connect" | "Reconnect") => {
        trackEvent(MixpanelEvent.ButtonClicked, {
            Location: "Connect Integration Card",
            Trigger: trigger,
            Provider: integration
        });

        connect({ type: "integration", provider: integration });
    }, [trackEvent, connect]);

    const handleDismiss = useCallback((integration: ConnectedIntegration) => {
        trackEvent(MixpanelEvent.ButtonClicked, {
            Location: "Connect Integration Card",
            Trigger: "Dismiss",
            Provider: integration
        });

        onDismiss?.(integrationRowKey(integration));
    }, [trackEvent, onDismiss]);

    if (rows.length === 0) {
        return null;
    }

    return (
        <StaggeredAnimation className="wai-connect-cards" display="flex" flexDirection="column" gap="inline-xs">
            {rows.map(({ integration, displayName, channelType, brandColor, brandForegroundColor, cardDescriptionKey }) => {
                const isConnected = connectedChannelTypes.has(channelType);
                const isDismissed = !isConnected && (dismissedKeys?.has(integrationRowKey(integration)) ?? false);
                // Text sitting on the brand plate. Light plates (Granola's olive) declare a dark
                // foreground; every other brand keeps the default white.
                const foregroundColor = brandForegroundColor ?? "core_samoyed";
                // The Connect button inverts the plate — a white fill — so its label defaults to the
                // brand color as ink, and takes the dark foreground when the plate declares one.
                const buttonLabelColor = brandForegroundColor ?? brandColor;
                const actionButton = (trigger: "Connect" | "Reconnect", labelKey: string) => (
                    <Button
                        variant="primary"
                        size="xs"
                        flexShrink={0}
                        backgroundColor="core_samoyed"
                        backgroundColorHover="transparent"
                        border="core_samoyed"
                        UNSAFE_color={buttonLabelColor}
                        UNSAFE_colorHover={foregroundColor}
                        onPress={() => handleConnect(integration, trigger)}
                    >
                        {tShared(labelKey)}
                    </Button>
                );

                // Only an open pause offers it, and only on a row that still needs the user.
                const dismissButton = onDismiss && !isDismissed && !(isConnected && !partiallyConnectedChannelTypes.has(channelType)) && (
                    <Button
                        variant="ghost-secondary"
                        size="xs"
                        flexShrink={0}
                        UNSAFE_color={foregroundColor}
                        onPress={() => handleDismiss(integration)}
                    >
                        {tShared("connectIntegration.dismiss")}
                    </Button>
                );

                return (
                    <StaggeredAnimationItem key={integration}>
                        <Flex
                            className="wai-connect-card"
                            gap="inline-sm"
                            paddingX="inset-md"
                            paddingY="inset-sm"
                            borderRadius="rounded-lg"
                            UNSAFE_backgroundColor={brandColor}
                        >
                            <Flex
                                alignItems="center"
                                justifyContent="center"
                                shrink={0}
                                backgroundColor="core_samoyed"
                                borderRadius="rounded-md"
                                UNSAFE_width="2rem"
                                UNSAFE_height="2rem"
                            >
                                <ConnectorChannelIcon
                                    type={channelType}
                                    UNSAFE_width="1.25rem"
                                    UNSAFE_height="1.25rem"
                                />
                            </Flex>
                            <Div className="wai-connect-card-body" data-testid="connect-card-body">
                                <Flex direction="column" minWidth={0}>
                                    <Div fontSize="body-sm" fontWeight="body-md-bold" color={foregroundColor}>
                                        {displayName}
                                    </Div>
                                    <Div fontSize="body-xs" color={foregroundColor}>
                                        {tShared(cardDescriptionKey)}
                                    </Div>
                                </Flex>
                                <Flex alignItems="center" wrap="wrap" gap="inline-sm" shrink={0}>
                                    {isDismissed ? (
                                        <Flex alignItems="center" shrink={0}>
                                            <Div fontSize="body-xs-semibold" color={foregroundColor}>
                                                {tShared("connectIntegration.dismissed")}
                                            </Div>
                                        </Flex>
                                    ) : partiallyConnectedChannelTypes.has(channelType) ? (
                                        <>
                                            <Div fontSize="body-xs-semibold" color={foregroundColor}>
                                                {tShared("connectIntegration.partiallyConnected")}
                                            </Div>
                                            {actionButton("Reconnect", "connectIntegration.reconnect")}
                                        </>
                                    ) : isConnected ? (
                                        <Flex alignItems="center" gap="inline-xs" color={foregroundColor} shrink={0}>
                                            <CheckmarkIcon size="sm" />
                                            <Div fontSize="body-xs-semibold" color={foregroundColor}>
                                                {tShared("connectIntegration.connected")}
                                            </Div>
                                        </Flex>
                                    ) : unavailableChannelTypes.has(channelType) ? (
                                        <Flex alignItems="center" shrink={0}>
                                            <Div fontSize="body-xs-semibold" color={foregroundColor}>
                                                {tShared("connectIntegration.disabledByAdmin")}
                                            </Div>
                                        </Flex>
                                    ) : actionButton("Connect", "connectIntegration.connect")}
                                    {dismissButton}
                                </Flex>
                            </Div>
                        </Flex>
                    </StaggeredAnimationItem>
                );
            })}
        </StaggeredAnimation>
    );
};
